/**
 * API service for RangeDat application
 * Handles all API calls to the backend API Gateway
 */
import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = 'https://api.rangedat.com/dev';

/**
 * Helper function to get the current authentication token
 * @returns {Promise<string|null>} - The JWT token or null if not authenticated
 */
export const getAuthToken = async () => {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch (error) {
    console.warn('Error getting auth token:', error);
    return null;
  }
};

/**
 * Helper function to handle API responses and errors
 * @param {Response} response - The fetch response object
 * @returns {Promise<Object>} - The parsed JSON response
 */
const handleApiResponse = async (response) => {
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    throw new Error(`API error (${response.status}): ${errorText}`);
  }
  return response.json();
};

/**
 * Create a mock response for development when API is unavailable
 * @param {string} endpoint - The API endpoint being called
 * @returns {Object} - A mock response object
 */
const createMockResponse = (endpoint) => {
  console.log(`Using mock data for endpoint: ${endpoint}`);

  // Mock session data
  const mockSessions = [
    {
      id: 'session-001',
      date: '2025-04-15',
      location: 'Pebble Beach Golf Links',
      launchMonitor: 'Trackman',
      shots: 75,
      averageDistance: 245,
      averageClubheadSpeed: 105,
      averageBallSpeed: 155,
      averageSmashFactor: 1.48
    },
    {
      id: 'session-002',
      date: '2025-04-10',
      location: 'Torrey Pines Golf Course',
      launchMonitor: 'GC Quad',
      shots: 60,
      averageDistance: 238,
      averageClubheadSpeed: 103,
      averageBallSpeed: 152,
      averageSmashFactor: 1.47
    },
    {
      id: 'session-003',
      date: '2025-04-05',
      location: 'Bethpage Black',
      launchMonitor: 'Flightscope',
      shots: 90,
      averageDistance: 242,
      averageClubheadSpeed: 104,
      averageBallSpeed: 154,
      averageSmashFactor: 1.48
    }
  ];

  // Return appropriate mock data based on endpoint
  if (endpoint.includes('/sessions/')) {
    const sessionId = endpoint.split('/').pop();
    return mockSessions.find(s => s.id === sessionId) || mockSessions[0];
  } else if (endpoint.includes('/sessions')) {
    return mockSessions;
  }

  return {};
};

/**
 * Fetch session details by ID
 * @param {string} sessionId - The ID of the session to fetch
 * @returns {Promise<Object>} - The session data
 */
export const fetchSessionById = async (sessionId) => {
  try {
    // Get authentication token
    const token = await getAuthToken();

    // Try to fetch from API first
    try {
      const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      return await handleApiResponse(response);
    } catch (apiError) {
      console.warn('API call failed, using mock data instead:', apiError);
      // If API call fails (including CORS issues), use mock data
      return createMockResponse(`${API_BASE_URL}/sessions/${sessionId}`);
    }
  } catch (error) {
    console.error('Error in fetchSessionById:', error);
    throw error;
  }
};

/**
 * Fetch all sessions for the current user
 * @returns {Promise<Array>} - Array of session data
 */
export const fetchAllSessions = async () => {
  try {
    // Get authentication token
    const token = await getAuthToken();

    // Try to fetch from API first
    try {
      const response = await fetch(`${API_BASE_URL}/sessions`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      return await handleApiResponse(response);
    } catch (apiError) {
      console.warn('API call failed, using mock data instead:', apiError);
      // If API call fails (including CORS issues), use mock data
      return createMockResponse(`${API_BASE_URL}/sessions`);
    }
  } catch (error) {
    console.error('Error in fetchAllSessions:', error);
    throw error;
  }
};

/**
 * Create a new session
 * @param {Object} sessionData - The session data to create
 * @returns {Promise<Object>} - The created session
 */
export const createSession = async (sessionData) => {
  try {
    // Get authentication token
    const token = await getAuthToken();
    if (!token) {
      throw new Error('Authentication required to create sessions');
    }

    // Try to fetch from API first
    try {
      const response = await fetch(`${API_BASE_URL}/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(sessionData)
      });

      return await handleApiResponse(response);
    } catch (apiError) {
      console.warn('API call failed, using mock response instead:', apiError);
      // If API call fails (including CORS issues), return mock response
      return {
        ...sessionData,
        id: `session-${Date.now()}`,
        created: new Date().toISOString()
      };
    }
  } catch (error) {
    console.error('Error in createSession:', error);
    throw error;
  }
};

/**
 * Update an existing session
 * @param {string} sessionId - The ID of the session to update
 * @param {Object} sessionData - The updated session data
 * @returns {Promise<Object>} - The updated session
 */
export const updateSession = async (sessionId, sessionData) => {
  try {
    // Get authentication token
    const token = await getAuthToken();
    if (!token) {
      throw new Error('Authentication required to update sessions');
    }

    // Try to fetch from API first
    try {
      const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(sessionData)
      });

      return await handleApiResponse(response);
    } catch (apiError) {
      console.warn('API call failed, using mock response instead:', apiError);
      // If API call fails (including CORS issues), return mock response
      return {
        ...sessionData,
        id: sessionId,
        updated: new Date().toISOString()
      };
    }
  } catch (error) {
    console.error('Error in updateSession:', error);
    throw error;
  }
};

/**
 * Delete a session
 * @param {string} sessionId - The ID of the session to delete
 * @returns {Promise<boolean>} - Success indicator
 */
export const deleteSession = async (sessionId) => {
  try {
    // Get authentication token
    const token = await getAuthToken();
    if (!token) {
      throw new Error('Authentication required to delete sessions');
    }

    // Try to fetch from API first
    try {
      const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      await handleApiResponse(response);
      return true;
    } catch (apiError) {
      console.warn('API call failed, using mock response instead:', apiError);
      // If API call fails (including CORS issues), return mock success
      return true;
    }
  } catch (error) {
    console.error('Error in deleteSession:', error);
    throw error;
  }
};
